import React from 'react'
import PageHeader from '@/components/Common/PageHeader'
import { contactInfo } from '@/data/contact'
import { Phone, Mail, MapPin, Clock, MessageCircle } from 'lucide-react'
import { toTelHref, toWhatsAppHref } from '@/utils/phone'
import SEOHead from '@/components/SEO/SEOHead'
import { buildGraph, breadcrumbListData, webPageSchema } from '@/data/structured-data'

const ContactPage: React.FC = () => {
  const mapSrc = `https://www.google.com/maps?q=${encodeURIComponent(contactInfo.address)}&output=embed`

  return (
    <>
      <SEOHead
        title="İletişim | Ardıç Veteriner Kliniği"
        description="Ardıç Veteriner Kliniği iletişim: Batıkent Mahallesi, Fuar Alanı Kavşağı, Karaköprü/Şanlıurfa. Telefon, WhatsApp, çalışma saatleri ve yol tarifi."
        keywords="veteriner telefon numarası, veteriner numaralari, şanlıurfa veteriner numarası, 7 24 veteriner whatsapp, veteriner adresleri, veteriner iletişim"
        canonicalUrl="/contact"
        structuredData={buildGraph(
          webPageSchema({
            name: 'İletişim',
            description: 'Ardıç Veteriner Kliniği telefon, WhatsApp, adres ve çalışma saatleri.',
            url: '/contact',
            type: 'ContactPage'
          }),
          breadcrumbListData([
            { name: 'Ana Sayfa', url: '/' },
            { name: 'İletişim', url: '/contact' }
          ])
        )}
      />
      <PageHeader title="İletişim" subtitle="Sorularınız ve randevu talepleriniz için bize ulaşın." />

      <section className="section-padding">
        <div className="container-custom grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Contact Cards */}
          <div className="space-y-4 order-2 lg:order-1">
            <div className="flex items-start space-x-4 p-6 bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl border border-primary-200">
              <div className="p-3 bg-primary-500 rounded-xl">
                <Phone className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-2">Telefon</h3>
                <a href={toTelHref(contactInfo.phone)} className="text-primary-600 hover:text-primary-700 font-semibold text-lg">
                  {contactInfo.phone}
                </a>
                <p className="text-gray-600 text-sm mt-1">Randevu ve bilgi için</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl border border-green-200">
              <div className="p-3 bg-green-500 rounded-xl">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-2">WhatsApp</h3>
                <a
                  href={toWhatsAppHref(contactInfo.whatsapp)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 hover:text-green-700 font-semibold text-lg"
                >
                  {contactInfo.whatsapp}
                </a>
                <p className="text-gray-600 text-sm mt-1">Hızlı iletişim için</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl border border-blue-200">
              <div className="p-3 bg-blue-500 rounded-xl">
                <Mail className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-2">E-posta</h3>
                <a href={`mailto:${contactInfo.email}`} className="text-blue-600 hover:text-blue-700 font-semibold text-lg">
                  {contactInfo.email}
                </a>
                <p className="text-gray-600 text-sm mt-1">Detaylı bilgi için</p>
              </div>
            </div>

            <div className="flex items-start space-x-4 p-6 bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl border border-orange-200">
              <div className="p-3 bg-orange-500 rounded-xl">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-gray-900 mb-2">Çalışma Saatleri</h3>
                <div className="space-y-2 text-gray-700">
                  <div className="flex justify-between"><span className="font-medium">Hafta içi:</span><span>{contactInfo.workingHours.weekdays}</span></div>
                  <div className="flex justify-between"><span className="font-medium">Hafta sonu:</span><span>{contactInfo.workingHours.weekend}</span></div>
                  <div className="flex justify-between border-t pt-2"><span className="font-semibold text-red-600">{contactInfo.workingHours.emergency}</span></div>
                </div>
              </div>
            </div>
          </div>

          {/* Map */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            <div className="bg-white rounded-2xl border shadow overflow-hidden">
              <div className="p-4 border-b flex items-center space-x-2">
                <MapPin className="w-5 h-5 text-primary-600" />
                <div className="text-gray-800 font-medium">{contactInfo.address}</div>
              </div>
              <iframe title="Harita" src={mapSrc} className="w-full h-[420px]" loading="lazy"></iframe>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ContactPage


